import { defineStore } from 'pinia'
import { useStorage } from '@vueuse/core'
interface User {
  id?: number
  email?: string
  name?: string
  api_token?: string
  longitude?: number
  latitude?: number
  tenant_identifier?: string
  api_tokenV2?: string
  tenant_identifierV2?: string
}
export const useUserStore = defineStore('userStore', () => {
  const { apiBaseUrl, apiBaseUrlV2 } = useAppConfig()
  const user = ref(useStorage('user', {} as User, localStorage))
  const calendarCategories = ref(
    useStorage('calendarCategories', [] as CalendarCategory[], localStorage)
  )
  const hasSupervisor = ref<boolean | null>(null)

  const contact = ref(
    useStorage('contact', {} as Contact & { token?: string; tenantId?: string }, localStorage)
  )

  const isLoggedIn = computed(() => !!user.value.api_token)
  const isLoggedInAsContact = computed(() => !!contact.value.token)
  const userEmail = computed(() => user.value.email ?? '-')
  const userProfilePicture = computed(() =>
    user?.value?.id
      ? `https:\\/\\/my.1tool.com\\/crm_files\\/users\\/${user.value.id}\\/logo.jpg`
      : 'https:\\/\\/my.1tool.com\\/images\\/standard_picture.jpg'
  )

  async function login(email: string, password: string): Promise<boolean> {
    const responseV2 = (await fetch(`${apiBaseUrlV2}auth/user`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        email,
        password,
        device_name: 'mac'
      }),
      cache: 'no-cache',
      mode: 'cors'
    }).catch(() => {
      useToast().add({
        severity: 'error',
        summary: 'stores.user.error.serviceNotReachable.title',
        detail: 'stores.user.error.serviceNotReachable.description'
      })
    })) as any

    if (!responseV2.ok) {
      throw new Error(responseV2.status)
    }

    const response = (await fetch(`${apiBaseUrl}auth/user`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({
        email,
        password,
        device_name: 'mac'
      }),
      cache: 'no-cache',
      mode: 'cors'
    }).catch(() => {
      useToast().add({
        severity: 'error',
        summary: 'stores.user.error.serviceNotReachable.title',
        detail: 'stores.user.error.serviceNotReachable.description'
      })
    })) as any

    if (!response.ok) {
      throw new Error(response.status)
    }

    if (responseV2 && responseV2.status === 200 && response && response.status === 200) {
      const responseText = await response.text()
      const responseTextV2 = await responseV2.text()

      if (responseText && responseTextV2) {
        const { api_token: apiTokenV2, tenant_identifier: tenantIdentifierV2 } =
          JSON.parse(responseTextV2)
        const {
          api_token: apiToken,
          tenant_identifier: tenantIdentifier,
          id
        } = JSON.parse(responseText)

        user.value = {
          id,
          email,
          api_token: apiToken,
          tenant_identifier: tenantIdentifier,
          api_tokenV2: apiTokenV2,
          tenant_identifierV2: tenantIdentifierV2
        }

        await fetchUserDetails()

        return true
      } else {
        return false
      }
    } else {
      return false
    }
  }

  async function logout() {
    user.value = {}
    const contactValue = { ...contact.value }

    // Clear current report when logging out
    const { clearCurrentReport } = useCurrentReport()
    clearCurrentReport()

    // Clear cached dropdown data
    const dropdownDataStore = useDropdownDataStore()
    dropdownDataStore.clearDropdownData()

    // Clear calendar permissions
    const calendarPermissionsStore = useCalendarPermissionsStore()
    calendarPermissionsStore.clearPermissions()

    await clearAllEvents()

    window?.localStorage?.clear()
    contact.value = contactValue
    navigateTo('/login')
  }

  async function fetchCalendarCategories(userIds?: number[]) {
    let ids: number[]

    if (userIds !== undefined) {
      ids = userIds
    } else {
      ids = user.value?.id ? [user.value.id] : []
    }

    try {
      let url = 'calendar-categories'
      if (ids.length > 0) {
        const param = encodeURIComponent(ids.join(','))
        url += `?filter[user_id]=${param}&per_page=1000`
      } else {
        url += '?per_page=1000'
      }

      const response = await useApi(url, 'GET')
      calendarCategories.value = response?.data ?? []
    } catch {
      calendarCategories.value = []
    }
  }

  function setHasSupervisor(hasSupervisorParam: boolean | null) {
    hasSupervisor.value = hasSupervisorParam
  }

  async function fetchUserDetails() {
    if (!user.value.id) return
    const response = await useApi(`users/${user.value.id}`, 'GET')
    if (response?.data?.name) {
      user.value = { ...user.value, name: response.data.name }
    }
  }

  async function setUserFromAzure(azureData: any) {
    user.value = {
      id: azureData.id,
      email: azureData.email,
      api_token: azureData.api_token,
      tenant_identifier: azureData.tenant_identifier || undefined,
      api_tokenV2: azureData.api_tokenV2 || undefined,
      tenant_identifierV2: azureData.tenant_identifierV2 || undefined
    }
    await fetchUserDetails()
  }

  return {
    user,
    userEmail,
    userProfilePicture,
    isLoggedIn,
    calendarCategories,
    hasSupervisor,
    contact,
    isLoggedInAsContact,
    fetchCalendarCategories,
    setHasSupervisor,
    login,
    setUserFromAzure,
    logout
  }
})
// Use HMR for this store
if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useUserStore, import.meta.hot))
}
